import React from 'react';
import PropTypes from 'prop-types';
import { formatDateTime, relativeTime } from './projectListModel';
import { NameLink, Owner, ReviewBar, SourceBadge, VendorMark, VersionPath } from './ProjectAtoms';
import { ProjectNameInput } from './ProjectNameEditor';
import ProjectActionsMenu from './ProjectActionsMenu';

const ProjectCard = ({ project, ctx, expanded, editing, canModify, actions }) => {
  const vendor = ctx.vendorLabel(project.vendorId);
  return (
    <div
      className={`pl-card${expanded ? ' is-expanded' : ''}`}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      data-pid={project.id}
      onClick={() => actions.toggle(project)}
      onDoubleClick={() => actions.open(project)}
      onKeyDown={e => {
        if (e.target !== e.currentTarget) return;
        // Space toggles; Enter expands a collapsed card (an expanded one is opened by the list's keyboard handler).
        if (e.key === ' ' || (e.key === 'Enter' && !expanded)) {
          e.preventDefault();
          actions.toggle(project);
        }
      }}
    >
      <div className="pl-card__top">
        <VendorMark vendorId={project.vendorId} label={vendor} size={36} />
        <div className="bz-grow pl-minw0">
          <div className="pl-card__eyebrow bz-truncate">
            {vendor} · {project.productName}
          </div>
          <div className="pl-card__name bz-truncate" title={project.name}>
            {editing ? (
              <ProjectNameInput
                name={project.name}
                onSave={name => actions.saveName(project, name)}
                onCancel={actions.cancelRename}
              />
            ) : (
              <NameLink project={project} onOpen={actions.open} />
            )}
          </div>
        </div>
        <ProjectActionsMenu project={project} canModify={canModify} actions={actions} />
      </div>
      <VersionPath project={project} versionLabel={ctx.versionLabel} />
      <ReviewBar project={project} available={ctx.availableVersions(project)} />
      <div className="pl-card__foot">
        <Owner userId={project.userId} currentUserEmail={ctx.currentUserEmail} />
        <span className="pl-dotsep">·</span>
        <span
          className="pl-nowrap"
          title={`Last activity ${formatDateTime(project.lastActivityAt || project.updatedAt)}`}
        >
          {relativeTime(project.lastActivityAt || project.updatedAt)}
        </span>
        <span className="bz-grow" />
        <SourceBadge project={project} />
      </div>
    </div>
  );
};

ProjectCard.propTypes = {
  project: PropTypes.object.isRequired,
  ctx: PropTypes.object.isRequired,
  expanded: PropTypes.bool.isRequired,
  editing: PropTypes.bool.isRequired,
  canModify: PropTypes.bool.isRequired,
  actions: PropTypes.object.isRequired
};

export default ProjectCard;
